import React from 'react';
import type { Aviso, NivelAviso } from '../types/metas.types';

interface Props {
  avisos: Aviso[];
  titulo?: string;
  /** Texto chico bajo el título (p. ej. «se recalculan al guardar»). */
  nota?: string;
  vacio?: string;
}

const ESTILO: Record<NivelAviso, { caja: string; icono: string; nombre: string }> = {
  error: { caja: 'border-red-200 bg-red-50 text-red-800', icono: 'pi pi-times-circle', nombre: 'Error' },
  advertencia: {
    caja: 'border-amber-200 bg-amber-50 text-amber-900',
    icono: 'pi pi-exclamation-triangle',
    nombre: 'Advertencia',
  },
  info: { caja: 'border-gray-200 bg-gray-50 text-gray-700', icono: 'pi pi-info-circle', nombre: 'Nota' },
};

const ORDEN: NivelAviso[] = ['error', 'advertencia', 'info'];

/**
 * Errores (bloquean el envío), advertencias (el regional las ve al autorizar)
 * e información. Los mensajes ya traen el NOMBRE de la persona: la API los
 * escribe así para que nadie tenga que traducir una llave a un renglón.
 */
export const AvisosLista: React.FC<Props> = ({ avisos, titulo, nota, vacio }) => {
  if (!avisos.length) {
    return vacio ? <div className="text-sm text-metas-texto-2">{vacio}</div> : null;
  }
  const ordenados = [...avisos].sort((a, b) => ORDEN.indexOf(a.nivel) - ORDEN.indexOf(b.nivel));
  const cuenta = (n: NivelAviso) => avisos.filter((a) => a.nivel === n).length;
  return (
    <div className="flex flex-col gap-2" data-testid="avisos-lista">
      {titulo && (
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h4 className="font-semibold text-gray-800">{titulo}</h4>
          <span className="text-sm text-metas-texto-2">
            {ORDEN.filter((n) => cuenta(n) > 0)
              .map((n) => `${cuenta(n)} ${n === 'error' ? 'error(es)' : n === 'advertencia' ? 'advertencia(s)' : 'nota(s)'}`)
              .join(' · ')}
          </span>
        </div>
      )}
      {nota && <div className="text-xs text-metas-texto-2">{nota}</div>}
      <ul className="flex flex-col gap-1">
        {ordenados.map((a, i) => {
          const e = ESTILO[a.nivel] ?? ESTILO.info;
          return (
            <li
              key={`${a.codigo}-${a.key ?? ''}-${i}`}
              className={`flex items-start gap-2 rounded border px-3 py-2 text-sm ${e.caja}`}
              data-nivel={a.nivel}
            >
              {/* El nivel, también en texto: un `aria-label` en un `<i>` (sin rol)
                  no llega al lector de pantalla, y el nivel quedaba sólo en el color. */}
              <i className={`${e.icono} mt-0.5`} aria-hidden="true" />
              <span>
                <span className="sr-only">{e.nombre}: </span>
                {a.mensaje}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AvisosLista;
